import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.category === slug);

    if (sortBy === 'hot') {
      result = [...result].sort((a, b) => {
        const aViews = parseInt(a.views.replace(/[^0-9]/g, ''));
        const bViews = parseInt(b.views.replace(/[^0-9]/g, ''));
        return bViews - aViews;
      });
    } else if (sortBy === 'new') {
      result = [...result].sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [slug, sortBy]);

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${currentCategory.name}漫画 - 韩漫网`,
    description: `韩漫网${currentCategory.name}漫画分类，${currentCategory.description}。精选优质韩国漫画免费在线观看。`,
    url: `https://hanmanwang.com/category/${slug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '韩漫网首页', item: 'https://hanmanwang.com/' },
        { '@type': 'ListItem', position: 2, name: `${currentCategory.name}漫画` },
      ],
    },
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory.name}漫画 - 韩国漫画大全 - 韩漫网`}
        description={`韩漫网${currentCategory.name}漫画分类，${currentCategory.description}。精选优质韩国漫画免费在线观看，每日更新。`}
        jsonLd={pageJsonLd}
      />
      <Navbar />
      <ScrollToTop />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          {/* Category Header */}
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
              <i className={`${currentCategory.icon} text-3xl`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              {currentCategory.name}漫画
            </h1>
            <p className="text-sm text-foreground-500">{currentCategory.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="mb-2">
            <div className="flex items-center gap-1 flex-wrap bg-background-100 rounded-full p-1 w-fit mx-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center justify-between py-4 border-b border-background-200/70 mb-6">
            <p className="text-xs text-foreground-500">
              共找到 <strong className="text-foreground-700">{filteredComics.length}</strong> 部{currentCategory.name}漫画
            </p>
            <div className="flex items-center gap-1">
              {[
                { key: 'hot' as const, label: '最热' },
                { key: 'new' as const, label: '最新' },
                { key: 'rating' as const, label: '评分' },
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSortBy(option.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    sortBy === option.key
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          <div className="pb-14">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画，请浏览其他分类</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}